import { InsurancePlan } from 'src/services/interfaces/insurance';
import { useApplicationStore } from 'src/store/application';
import { useAuthStore } from 'src/store/auth';

const equitableSrc = "/images/equitablelogo.svg";

interface EquitableCardProps {
  side?: 'front' | 'back';
  plan?: InsurancePlan;
  firstName?: string;
  lastName?: string;
  memberId?: string | number;
  groupId?: string | number
}

export const EquitableCard = ({ side = 'front', plan, firstName, lastName, memberId, groupId }: EquitableCardProps) => {
  const {
    submittedAt
  } = useApplicationStore();
  const memberData = {
    subscriberName: `${firstName || ''} ${lastName || ''}`.trim() || '{Subscriber name}',
    planName: plan?.name || '{Plan Name}',
    deductible: '$50',
    groupNumber: groupId || '21729',
    memberNumber: memberId || '{0000000000000}',
    planType: 'PPO',
    effectiveDate: '{MM/DD/YY}',
  };

  if (side === 'back') {
    return (
      <div className="relative aspect-[672/393] max-w-[672px] origin-center scale-60 md:scale-100 border border-[#e0e0e0] rounded-2xl system-font px-4">
        {/* Header Section */}
        <div className="pt-4 px-4">
          <img src="/images/equitable-card.png" alt="Equitable" className='h-15' />
          <div className='my-2'>
            <p className="text-sm text-black font-normal">
              Equitable Financial Life Insurance Company of America
            </p>
          </div>
        </div>

        {/* Claims Submission Section */}
        <div className="px-4 ml-16">
          <p className="text-base text-black font-bold">
            PLEASE submit claims using Payor ID 78077 or mail to
          </p>
          <div className="text-base text-black">
            <div>Dental Claims Administrator</div>
            <div>PO Box 69421</div>
            <div>Harrisburg, PA 17106-9421</div>
          </div>
        </div>

        {/* Provider Portal Section */}
        <div className="px-4">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <p className="text-base text-black font-bold mt-2">
                Visit our Provider Portal or use QR Code for patient information:
              </p>
              <a
                href="https://www.equitabledental.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base text-[#1b2f6f] underline"
              >
                www.equitabledental.com
              </a>
            </div>
            {/* QR Code */}
            <div className="w-24 h-24 flex items-center justify-center flex-shrink-0">
              <img src="/images/equitable-qr.png" alt="QR Code" className="w-full h-full" />
            </div>
          </div>
        </div>

        {/* Affiliated Networks Section */}
        <div className="px-4">
          <p className="text-base text-black font-bold">
            All affiliated networks list at:
          </p>
          <a
            href="https://www.equitable.com/dentalprovider"
            target="_blank"
            rel="noopener noreferrer"
            className="text-base text-[#1b2f6f] underline"
          >
            www.equitable.com/dentalprovider
          </a>
        </div>

        {/* Bottom Disclaimers */}
        <div className="grid grid-cols-4 pb-4">
          <div />
          <div />
          <p className="text-black font-semibold text-[8px] mr-2">
            Access in your market provided by Dental Benefit Providers, Inc
          </p>
          <p className="text-[8px] font-semibold text-black text-left">
            Administered by United Concordia Dental Underwritten by Equitable
          </p>
        </div>

      </div>
    );
  }

  return (
    <div className="relative aspect-[672/409] max-w-[672px] origin-center scale-60 md:scale-100 border border-[#e0e0e0] rounded-2xl system-font px-4">
      {/* Header Section */}
      <div className="pt-4 px-4">
        {/* Logo Circle */}
        <img src="/images/equitable-card.png" alt="Equitable" className='h-15' />
        {/* Company Name */}
        <div className='my-2'>
          <p className="text-sm text-black font-normal">
            Equitable Financial Life Insurance Company of America
          </p>
        </div>
      </div>

      {/* Main Information Section - Two Columns */}
      <div className="flex gap-8 px-4">
        {/* Left Column */}
        <div className="flex-1">
          <div className="text-base text-black">
            <span className="font-bold">Subscriber: </span>
            <span className="font-normal text-[#1b2f6f]">{memberData.subscriberName}</span>
          </div>
          <div className="text-base text-black">
            <span className="font-bold">Coverage: </span>
            <span className="font-normal text-[#1b2f6f]">{memberData.planName}</span>
          </div>
          <div className="text-base text-black">
            <span className="font-bold">Deductible: </span>
            <span className="font-normal text-[#1b2f6f]">{memberData.deductible}</span>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex-1">
          <div className="text-base text-black">
            <span className="font-bold">Group Number: </span>
            <span className="font-normal text-[#1b2f6f]">{memberData.groupNumber}</span>
          </div>
          <div className="text-base text-black">
            <span className="font-bold">Member Number: </span>
            <span className="font-normal text-[#1b2f6f]">{memberData.memberNumber}</span>
          </div>
          <div className="text-base text-black">
            <span className="font-bold">Plan Type: </span>
            <span className="font-normal text-[#1b2f6f]">{memberData.planType}</span>
          </div>
          <div className="text-base text-black">
            <span className="font-bold">Effective Date: </span>
            <span className="font-normal text-[#1b2f6f]">{submittedAt ? new Date(submittedAt).toLocaleDateString('en-US') : 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Dentist Search Section */}
      <div className="px-4">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <p className="text-base text-black font-bold">
              To find a participating Dentist, visit the website or scan QR Code:
            </p>
            <a
              href="https://www.equitable.com/finddentist"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base text-[#1b2f6f] underline"
            >
              www.equitable.com/finddentist
            </a>
          </div>
          {/* QR Code */}
          <div className="w-24 h-24 flex items-center justify-center flex-shrink-0">
            <img src="/images/equitable-qr.png" alt="QR Code" className="w-24 h-24" />
          </div>
        </div>
      </div>

      {/* Customer Service Section */}
      <div className="px-4">
        <p className="text-base text-black font-bold">
          For eligibility, claim or provider participation status, call:
        </p>
        <p className="text-base text-black">
          Customer Service at 1-866-274-9887
        </p>
      </div>

      {/* Disclaimer Section */}
      <div className="px-4 pb-4 mt-4">
        <p className="text-black font-bold">
          This card is for informational purposes only and does NOT guarantee coverage.
        </p>
      </div>
    </div>
  );
};


